import { create } from 'zustand'
import * as THREE from 'three'
import { WorldSeed, PortalState, PortalConfig } from '../types/world'
import type { LobbyMessage } from '../systems/LobbyClient'

export type ChatMessageRole = 'user' | 'assistant' | 'system'
export type ChatChannel = 'sovereign' | 'lobby'

export interface ChatMessage {
  role: ChatMessageRole
  content: string
  hasJsonTool?: boolean
  worldSeed?: WorldSeed
}

export interface GenesisState {
  // Scene state
  isInteracting: boolean
  setIsInteracting: (interacting: boolean) => void
  
  // Titan state
  titanBeamActive: boolean
  triggerTitanBeam: () => void
  
  // Portal state
  activePortal: PortalConfig | null
  portalState: PortalState
  worldSeed: WorldSeed | null
  generationProgress: number
  spawnPortal: (game: string, color: string, worldSeed?: WorldSeed) => void
  updatePortalState: (state: PortalState) => void
  setWorldSeed: (seed: WorldSeed | null | undefined) => void
  setGenerationProgress: (progress: number) => void
  dismissPortal: () => void
  isPortalOpen: boolean
  setPortalOpen: (open: boolean) => void
  
  // Neural calibration state (WebLLM model loading)
  neuralLoadProgress: number       // 0-100
  isNeuralReady: boolean
  neuralLoadStatus: string         // current status text
  setNeuralLoadProgress: (progress: number) => void
  setNeuralReady: (ready: boolean) => void
  setNeuralLoadStatus: (status: string) => void
  
  // Aether environmental state (Tatiana-controllable)
  coreIntensity: number           // eye emissive intensity (default 1.2)
  ringSpeed: number               // ophanim rotation multiplier (default 1.0)
  voidTheme: string               // "starlight" | "nebula" | "pitch_black"
  lastObservation: string | null  // sensor feedback from the 3D world
  setLastObservation: (obs: string | null) => void
  updateAether: (params: Partial<{ coreIntensity: number; ringSpeed: number; voidTheme: string }>) => void
  
  // Tatiana state
  isTatianaTyping: boolean
  setTatianaTyping: (typing: boolean) => void
  
  // AI processing state
  isAiProcessing: boolean
  setAiProcessing: (processing: boolean) => void
  
  // Chat state
  chatMessages: ChatMessage[]
  addChatMessage: (message: ChatMessage) => void
  addSystemMessage: (content: string) => void
  clearChat: () => void
  
  // UI visibility
  uiVisible: boolean
  setUiVisible: (visible: boolean) => void
  
  // Singularity entry state
  hasInitialized: boolean
  setHasInitialized: (initialized: boolean) => void
  
  // Camera state for spatial navigation
  cameraPosition: THREE.Vector3
  cameraRotation: THREE.Euler
  setCameraPosition: (position: THREE.Vector3) => void
  setCameraRotation: (rotation: THREE.Euler) => void
  
  // Pointer lock state
  isPointerLocked: boolean
  setPointerLocked: (locked: boolean) => void
  
  // Portal collision state
  isInPortal: boolean
  currentPortalGame: string | null
  setInPortal: (inPortal: boolean, game?: string | null) => void
  
  // Active game state (for WASM hot-swap)
  activeGame: string | null
  isGameLoading: boolean
  loadGame: (game: string) => void
  exitGame: () => void
  
  // Audio state
  audioEnabled: boolean
  isTatianaSpeaking: boolean
  setAudioEnabled: (enabled: boolean) => void
  toggleAudio: () => boolean
  setTatianaSpeaking: (speaking: boolean) => void
  
  // Lobby / Channel state
  chatChannel: ChatChannel
  setChatChannel: (channel: ChatChannel) => void
  lobbyMessages: LobbyMessage[]
  setLobbyMessages: (messages: LobbyMessage[]) => void
  addLobbyMessage: (message: LobbyMessage) => void
  lobbyConnected: boolean
  setLobbyConnected: (connected: boolean) => void
  lobbyNodeName: string
  setLobbyNodeName: (name: string) => void
}

export const useGenesisState = create<GenesisState>((set, get) => ({
  // Scene state
  isInteracting: false,
  setIsInteracting: (interacting) => set({ isInteracting: interacting }),
  
  // Titan state
  titanBeamActive: false,
  triggerTitanBeam: () => {
    set({ titanBeamActive: true })
    setTimeout(() => set({ titanBeamActive: false }), 1500)
  },
  
  // Portal state
  activePortal: null,
  portalState: 'dormant',
  worldSeed: null,
  generationProgress: 0,
  spawnPortal: (game, color, worldSeed) => {
    const portal: PortalConfig = {
      game,
      color,
      position: [0, 2, -4],
      active: true,
      state: 'generating',
      worldSeed: worldSeed || undefined,
      generationProgress: 0
    }
    set({ 
      activePortal: portal,
      portalState: 'generating',
      worldSeed: worldSeed || undefined,
      generationProgress: 0,
      isAiProcessing: true
    })
  },
  updatePortalState: (state) => {
    const currentPortal = get().activePortal
    if (currentPortal) {
      set({
        portalState: state,
        activePortal: { ...currentPortal, state },
        isAiProcessing: state === 'generating'
      })
    } else {
      set({ portalState: state })
    }
  },
  setWorldSeed: (seed) => set({ worldSeed: seed }),
  setGenerationProgress: (progress) => {
    const currentPortal = get().activePortal
    if (currentPortal) {
      set({
        generationProgress: progress,
        activePortal: { ...currentPortal, generationProgress: progress }
      })
    } else {
      set({ generationProgress: progress })
    }
  },
  dismissPortal: () => set({
    activePortal: null,
    portalState: 'dormant',
    worldSeed: null,
    generationProgress: 0
  }),
  isPortalOpen: false,
  setPortalOpen: (open) => set({ isPortalOpen: open }),
  
  // Neural calibration state
  neuralLoadProgress: 0,
  isNeuralReady: false,
  neuralLoadStatus: 'Awaiting initialization...',
  setNeuralLoadProgress: (progress) => set({ neuralLoadProgress: progress }),
  setNeuralReady: (ready) => set({ isNeuralReady: ready }),
  setNeuralLoadStatus: (status) => set({ neuralLoadStatus: status }),
  
  // Aether environmental state
  coreIntensity: 1.2,
  ringSpeed: 1.0,
  voidTheme: 'starlight',
  lastObservation: null,
  setLastObservation: (obs) => set({ lastObservation: obs }),
  updateAether: (params) => set(params),
  
  // Tatiana state
  isTatianaTyping: false,
  setTatianaTyping: (typing) => set({ isTatianaTyping: typing }),
  
  // AI processing state
  isAiProcessing: false,
  setAiProcessing: (processing) => set({ isAiProcessing: processing }),
  
  // Chat state
  chatMessages: [],
  addChatMessage: (message) => 
    set((state) => ({ 
      chatMessages: [...state.chatMessages, message].slice(-50)
    })),
  addSystemMessage: (content) =>
    set((state) => ({
      chatMessages: [...state.chatMessages, { role: 'system' as const, content }].slice(-50)
    })),
  clearChat: () => set({ chatMessages: [] }),
  
  // UI visibility
  uiVisible: true,
  setUiVisible: (visible) => set({ uiVisible: visible }),
  
  // Singularity entry state
  hasInitialized: false,
  setHasInitialized: (initialized) => set({ hasInitialized: initialized }),
  
  // Camera state
  cameraPosition: new THREE.Vector3(0, 0, 5),
  cameraRotation: new THREE.Euler(0, 0, 0, 'YXZ'),
  setCameraPosition: (position) => set({ cameraPosition: position }),
  setCameraRotation: (rotation) => set({ cameraRotation: rotation }),
  
  // Pointer lock state
  isPointerLocked: false,
  setPointerLocked: (locked) => set({ isPointerLocked: locked }),
  
  // Portal collision state
  isInPortal: false,
  currentPortalGame: null,
  setInPortal: (inPortal, game = null) => set({ 
    isInPortal: inPortal, 
    currentPortalGame: game,
    portalState: inPortal ? 'active' : get().portalState
  }),
  
  // Active game state
  activeGame: null,
  isGameLoading: false,
  loadGame: (game) => {
    set({ isGameLoading: true, activeGame: game })
    setTimeout(() => {
      set({ isGameLoading: false })
    }, 1500)
  },
  exitGame: () => set({ activeGame: null, isGameLoading: false, isInPortal: false, currentPortalGame: null }),
  
  // Audio state
  audioEnabled: false,
  isTatianaSpeaking: false,
  setAudioEnabled: (enabled) => set({ audioEnabled: enabled }),
  toggleAudio: () => {
    const newState = !get().audioEnabled;
    set({ audioEnabled: newState });
    return newState;
  },
  setTatianaSpeaking: (speaking) => set({ isTatianaSpeaking: speaking }),
  
  // Lobby / Channel state
  chatChannel: 'sovereign',
  setChatChannel: (channel) => set({ chatChannel: channel }),
  lobbyMessages: [],
  setLobbyMessages: (messages) => set({ lobbyMessages: messages }),
  addLobbyMessage: (message) =>
    set((state) => ({
      lobbyMessages: [...state.lobbyMessages, message].slice(-100)
    })),
  lobbyConnected: false,
  setLobbyConnected: (connected) => set({ lobbyConnected: connected }),
  lobbyNodeName: 'SOVEREIGN_' + Math.random().toString(36).slice(2, 6).toUpperCase(),
  setLobbyNodeName: (name) => set({ lobbyNodeName: name }),
}))

export default useGenesisState
